var _excluded = ["disabled", "edges", "dropzoneLabel", "dropzoneDescription", "placeholder", "onSave", "onDelete", "value", "DropzoneIcon", "hasSelection"];
var _templateObject, _templateObject2, _templateObject3, _templateObject4;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function _taggedTemplateLiteralLoose(strings, raw) { if (!raw) { raw = strings.slice(0); } strings.raw = raw; return strings; }
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import LinearProgress from '@material-ui/core/LinearProgress';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonIcon from '@material-ui/icons/Person';
import WarningIcon from '@material-ui/icons/Warning';
import { useDropzone } from 'react-dropzone';
import ActionArea from '../ActionArea';
import Confirmation from '../Confirmation';
import Modal from '../Modal';
import ModalContent from '../ModalContent';
import ModalHeader from '../ModalHeader';
import Text from '../Text';
import AvatarCropDialog from './AvatarCropDialog';
import { mixColors, hexToRgba } from '../../utils/colors';
var _borderRadius = function borderRadius(edges, theme) {
  switch (edges) {
    case 'rounded':
      return 99;
    case 'sharp':
      return 0;
    default:
      return theme.shape.borderRadius;
  }
};
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      color: theme.palette.text.primary,
      position: 'relative',
      border: function border(_ref) {
        var hasErrors = _ref.hasErrors;
        return "2px dashed " + (hasErrors ? theme.palette.error.main : theme.palette.primary.main);
      },
      background: function background(_ref2) {
        var disabled = _ref2.disabled;
        return disabled ? theme.palette.grey[300] : null;
      },
      opacity: function opacity(_ref3) {
        var disabled = _ref3.disabled;
        return disabled ? 0.5 : 1;
      },
      borderRadius: function borderRadius(_ref4) {
        var edges = _ref4.edges;
        return _borderRadius(edges, theme);
      },
      '& img': {
        width: '100%',
        height: 'auto',
        display: 'block'
      },
      '&:focus': {
        border: "2px solid " + theme.palette.primary.main,
        outline: 'none',
        background: hexToRgba(theme.palette.primary.main, 0.1)
      },
      '& .ReactCrop__crop-selection::before': {
        content: "''"
      }
    },
    dragging: {
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%',
      opacity: 0.2,
      backgroundColor: function backgroundColor(_ref5) {
        var isDragReject = _ref5.isDragReject;
        return isDragReject ? theme.palette.error.main : theme.palette.primary.main;
      },
      '& .MuiLinearProgress-barColorPrimary': {
        background: function background(_ref6) {
          var isDragReject = _ref6.isDragReject;
          return mixColors(isDragReject ? theme.palette.error.main : theme.palette.primary.main, '#ffffff', 60);
        }
      }
    },
    icon: {
      textAlign: 'center',
      '& svg': {
        width: '100%',
        maxWidth: 150,
        height: 'auto',
        color: theme.palette.grey.main,
        display: 'block'
      },
      '& .icon-border': {
        display: 'inline-block',
        padding: 20,
        marginBottom: 10,
        height: 110,
        width: 110,
        position: 'relative'
      }
    },
    avatar: {
      backgroundColor: theme.palette.grey.transparent,
      borderRadius: '50%',
      overflow: 'hidden',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      height: '100%',
      width: '100%'
    },
    loading: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -20,
      marginLeft: -20
    }
  };
});
var StyledDeleteIcon = styled.span(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref7) {
  var theme = _ref7.theme;
  return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n    display: inline-flex;\n    justify-content: center;\n    align-items: center;\n    position: absolute;\n    top: 50%;\n    right: 25px;\n    z-index: 9;\n    background: transparent;\n    color: ", ";\n    width: 48px;\n    height: 48px;\n    margin-top: -12px;\n    font-size: 20px;\n    transition: all 0.25s ease-in-out;\n    transform: translateY(-50%) scale(0.9);\n    box-shadow: ", ";\n    border-radius: 50%;\n    &:hover {\n      background: rgba(0, 0, 0, 0.04);\n      transform: translateY(-50%) scale(1.05);\n      color: ", ";\n      box-shadow: ", ";\n    }\n  "])), theme.palette.grey.main, theme.shadows[2], theme.palette.secondary.main, theme.shadows[4]);
});
var StyledAddIcon = styled.span(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref8) {
  var theme = _ref8.theme;
  return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["\n    display: inline-flex;\n    width: 12px;\n    height: 12px;\n    justify-content: center;\n    align-items: center;\n    border-radius: 50%;\n    background: ", ";\n    color: ", ";\n    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.4);\n    position: absolute;\n    right: 12px;\n    z-index: 2;\n    bottom: 8px;\n  "])), theme.palette.background.paper, theme.palette.primaryAlt.main);
});
var AvatarInput = function AvatarInput(_ref9) {
  var disabled = _ref9.disabled,
    edges = _ref9.edges,
    dropzoneLabel = _ref9.dropzoneLabel,
    dropzoneDescription = _ref9.dropzoneDescription,
    placeholder = _ref9.placeholder,
    onSave = _ref9.onSave,
    onDelete = _ref9.onDelete,
    value = _ref9.value,
    DropzoneIcon = _ref9.DropzoneIcon,
    hasSelection = _ref9.hasSelection,
    rest = _objectWithoutPropertiesLoose(_ref9, _excluded);
  var theme = useTheme();
  var isNotMobile = useMediaQuery(theme.breakpoints.up('sm'));
  var _useState = useState(false),
    confirmDelete = _useState[0],
    setConfirmDelete = _useState[1];
  var _useState2 = useState(null),
    droppedFile = _useState2[0],
    setDroppedFile = _useState2[1];
  var _useState3 = useState(false),
    open = _useState3[0],
    setOpen = _useState3[1];
  var _useState4 = useState(''),
    savedImage = _useState4[0],
    setSavedImage = _useState4[1];
  var _useState5 = useState(false),
    loading = _useState5[0],
    setLoading = _useState5[1];
  var onDrop = function onDrop(acceptedFiles) {
    setLoading(true);
    var reader = new FileReader();
    reader.addEventListener('load', function () {
      setDroppedFile(reader.result);
      setOpen(true);
      setLoading(false);
    });
    reader.readAsDataURL(acceptedFiles[0]);
  };
  var handleSave = function handleSave(result, blob) {
    return onSave(result, blob).then(function () {
      setSavedImage(result);
      setOpen(false);
    })
    // eslint-disable-next-line no-console
    .catch(function (e) {
      return console.error(e);
    });
  };
  var handleDelete = function handleDelete() {
    return onDelete().then(function () {
      setDroppedFile(null);
      setSavedImage('');
      setConfirmDelete(false);
      setOpen(false);
    });
  };
  var _useDropzone = useDropzone({
      onDrop: onDrop,
      disabled: disabled,
      accept: 'image/*',
      maxFiles: 1
    }),
    getRootProps = _useDropzone.getRootProps,
    getInputProps = _useDropzone.getInputProps,
    isDragActive = _useDropzone.isDragActive,
    isDragAccept = _useDropzone.isDragAccept,
    isDragReject = _useDropzone.isDragReject,
    fileRejections = _useDropzone.fileRejections;
  var hasErrors = fileRejections.length > 0;
  var classes = useStyles({
    edges: edges,
    disabled: disabled,
    hasErrors: hasErrors,
    isDragReject: isDragReject
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(Box, _extends({
    className: classes.root
  }, getRootProps(), {
    style: {
      padding: '1rem 1.5rem 0.8rem 1.5rem',
      marginBottom: '0.25rem'
    }
  }), isDragActive && /*#__PURE__*/React.createElement(LinearProgress, {
    className: classes.dragging
  }), loading && !hasErrors && /*#__PURE__*/React.createElement(CircularProgress, {
    className: classes.loading
  }), /*#__PURE__*/React.createElement("input", getInputProps()), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 5,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 3,
    md: 2,
    className: classes.icon
  }, /*#__PURE__*/React.createElement("div", {
    className: "icon-border"
  }, function () {
    if (isDragReject) {
      return /*#__PURE__*/React.createElement(WarningIcon, null);
    }
    if (isDragAccept) {
      return /*#__PURE__*/React.createElement(CheckCircleIcon, null);
    }
    if (savedImage || value || placeholder) {
      return /*#__PURE__*/React.createElement("div", {
        className: classes.avatar,
        style: {
          backgroundImage: "url(" + (savedImage || value || placeholder) + ")"
        }
      });
    }
    if (DropzoneIcon) {
      return /*#__PURE__*/React.createElement(DropzoneIcon, null);
    }
    return /*#__PURE__*/React.createElement(PersonIcon, null);
  }(), !hasSelection && /*#__PURE__*/React.createElement(StyledAddIcon, null, /*#__PURE__*/React.createElement(AddIcon, {
    style: {
      color: 'inherit'
    }
  }))), /*#__PURE__*/React.createElement(Text, {
    align: "center",
    weight: "500",
    color: "textPrimary",
    noWrap: true,
    gutterBottom: false
  }, isDragReject ? 'Error!' : dropzoneLabel)), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 8,
    md: 9
  }, /*#__PURE__*/React.createElement(Box, {
    pl: isNotMobile ? 5 : 0
  }, /*#__PURE__*/React.createElement(Text, {
    align: isNotMobile ? 'left' : 'center',
    weight: "500",
    color: "textPrimary"
  }, isDragReject ? 'The selected file(s) will not be accepted' : dropzoneDescription))))), (savedImage || value) && /*#__PURE__*/React.createElement(ActionArea, {
    onClick: function onClick() {
      return setConfirmDelete(true);
    }
  }, /*#__PURE__*/React.createElement(StyledDeleteIcon, null, /*#__PURE__*/React.createElement(DeleteIcon, null)))), droppedFile && /*#__PURE__*/React.createElement(Modal, {
    open: open,
    onClose: function onClose() {
      return setOpen(false);
    },
    fullWidth: true,
    maxWidth: "lg"
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    onClose: function onClose() {
      return setOpen(false);
    }
  }, /*#__PURE__*/React.createElement("span", null, "\xA0")), /*#__PURE__*/React.createElement(ModalContent, null, /*#__PURE__*/React.createElement(AvatarCropDialog, _extends({
    droppedFile: droppedFile
  }, rest, {
    onSave: handleSave,
    onDelete: handleDelete
  })))), /*#__PURE__*/React.createElement(Confirmation, {
    open: confirmDelete,
    onCancel: function onCancel() {
      return setConfirmDelete(false);
    },
    onConfirm: handleDelete,
    message: "Are you sure you want to remove the attached image?"
  }));
};
AvatarInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If `true`, the label, input and helper text should be displayed in a disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * Text label displayed in the dropzone
   */
  dropzoneLabel: PropTypes.string,
  /**
   * Text description displayed in the dropzone
   */
  dropzoneDescription: PropTypes.any,
  /**
   * Icon displayed in the dropzone
   */
  DropzoneIcon: PropTypes.any,
  /**
   * URL for default/placeholder image.
   */
  placeholder: PropTypes.string,
  /**
   * URL for current image.
   */
  value: PropTypes.string,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp']),
  /**
   * Do something when user saves cropped image, received the base64 string
   */
  onSave: PropTypes.func,
  /**
   * Do something when user removes image
   */
  onDelete: PropTypes.func,
  /**
   * If `true`, represents the file(s) has been selected
   */
  hasSelection: PropTypes.bool
} : {};
AvatarInput.defaultProps = {
  disabled: false,
  dropzoneLabel: 'Upload file',
  dropzoneDescription: 'Drag and drop a file here, or click to select a file',
  edges: 'normal',
  placeholder: 'https://via.placeholder.com/500x300',
  value: '',
  onSave: function onSave() {
    return Promise.resolve(true);
  },
  onDelete: function onDelete() {
    return Promise.resolve(true);
  },
  DropzoneIcon: null,
  hasSelection: false
};
export default AvatarInput;